import Flickity from "flickity";
import "flickity-imagesloaded";
import api from "@/app/javascript/api";
import { on, ready } from "../utils/listeners";
import ImageGalleryZoom from "./imageGalleryZoom";

const QTY_PICKERS =
  ".js-large-quantity-picker-mobile, .js-large-quantity-picker-desktop";

let cardThickness;

const setupImageGalleryZoom = () => {
  const imageGalleryZoom = new ImageGalleryZoom();
  imageGalleryZoom.init();
};

const showMoreGalleryImagesPdp = () => {
  const showMoreLink = document.querySelector(".js-show-more-link");
  const showMoreLinkText = document.querySelector(".js-show-more-link-text");
  const pdpGallery = document.querySelector(".product-page-carousel-container");
  if (showMoreLink) {
    showMoreLink.addEventListener("click", () => {
      if (pdpGallery.classList.contains("expanded")) {
        pdpGallery.classList.remove("expanded");
        showMoreLinkText.innerText = "Show more";
      } else {
        const analyticsId = document.getElementById("product-page-analytics")
          ?.dataset?.analyticsId;

        analytics.track("Clicked Show More On Image Gallery", {
          analytics_id: analyticsId
        });
        pdpGallery.classList.add("expanded");
        showMoreLinkText.innerText = "Show less";
      }
    });
  }
};

const changeCustomiseUrl = options => {
  const customiseButtons = document.querySelectorAll(
    ".js-listing-customise-cta"
  );

  customiseButtons.forEach(customiseButton => {
    const customiseUrl = new URL(customiseButton.href);
    const { thick } = options;

    if (thick !== undefined) {
      customiseUrl.searchParams.set("thick", thick);
    }
    customiseButton.href = customiseUrl.toString();
  });
};

// $screen-md-min — the `md:` breakpoint
const desktopMediaQuery = window.matchMedia("(min-width: 992px)");

const getVisibleQtyPicker = () =>
  desktopMediaQuery.matches
    ? document.querySelector(".js-large-quantity-picker-desktop")
    : document.querySelector(".js-large-quantity-picker-mobile");

const setupThicknessPricingDifference = () => {
  const selectedOption = getVisibleQtyPicker()?.querySelector(
    ".js-listing-page-qty-option.selected"
  );
  const thicknessAdditionalCostEls = document.querySelectorAll(
    ".js-thickness-additional-cost"
  );
  if (selectedOption && thicknessAdditionalCostEls.length) {
    const { thicknessAdditionalCost } = selectedOption.dataset;
    thicknessAdditionalCostEls.forEach(el => {
      el.innerText = ` +${thicknessAdditionalCost}/ea`;
    });
  }
};

const updateSelectedQuantityOption = ({ thick }) => {
  const visiblePicker = getVisibleQtyPicker();
  const selectedOption = visiblePicker?.querySelector(
    ".js-listing-page-qty-option.selected"
  );

  if (selectedOption) {
    const { qty } = selectedOption.dataset;

    const selector = thick
      ? `.js-listing-page-qty-option.thick-option[data-qty="${qty}"]`
      : `.js-listing-page-qty-option:not(.thick-option)[data-qty="${qty}"]`;

    const equivalentSelectedOption = visiblePicker?.querySelector(selector);

    if (
      equivalentSelectedOption &&
      equivalentSelectedOption !== selectedOption
    ) {
      equivalentSelectedOption.click();
    }
  }
};

const changeWeddingPricing = ({ thick }) => {
  // Apply thickness-enabled to all pickers so CSS shows the right rows on both breakpoints
  document.querySelectorAll(QTY_PICKERS).forEach(table => {
    table.classList.toggle("thickness-enabled", thick);
  });

  // Must run before stripping so it can still find the currently-selected element
  updateSelectedQuantityOption({ thick });

  // Strip any lingering hidden selections from all pickers
  document.querySelectorAll(QTY_PICKERS).forEach(table => {
    const hiddenSelected = thick
      ? table.querySelectorAll(
          ".listing-page-qty-option:not(.thick-option).selected"
        )
      : table.querySelectorAll(
          ".listing-page-qty-option.thick-option.selected"
        );
    hiddenSelected.forEach(el => {
      el.classList.remove("selected", "inset-border-ink", "text-ink");
      el.classList.add("text-ink-4");
    });
  });
};

const clearThickState = () => {
  const customiseButtons = document.querySelectorAll(
    ".js-listing-customise-cta"
  );
  customiseButtons.forEach(btn => {
    const url = new URL(btn.href);
    url.searchParams.delete("thick");
    btn.href = url.toString();
  });

  const visiblePicker = getVisibleQtyPicker();
  const selectedQty = visiblePicker?.querySelector(
    ".js-listing-page-qty-option.selected"
  );
  if (selectedQty) {
    const { qty } = selectedQty.dataset;
    const nonThick =
      visiblePicker.querySelector(
        `.js-listing-page-qty-option:not(.thick-option)[data-qty="${qty}"]`
      ) ||
      visiblePicker.querySelector(
        `.js-listing-page-qty-option:not(.thick-option)`
      );

    if (nonThick && nonThick !== selectedQty) nonThick.click();
  }
  document
    .querySelectorAll(QTY_PICKERS)
    .forEach(el => el.classList.remove("thickness-enabled"));
};

const updateThickPriceOnVariantChange = () => {
  if (!cardThickness) return;

  const thickInput = document.querySelector(
    '#thickness-options input[type="radio"][value="double"], #thickness-options-mobile input[type="radio"][value="double"]'
  );

  if (thickInput) {
    changeWeddingPricing({ thick: true });
    changeCustomiseUrl({ thick: true });
    thickInput.checked = true;
  } else {
    clearThickState();
  }
};

const setupExplainerDrawerEventListener = () => {
  const openDrawerLinks = document.querySelectorAll(
    ".js-explain-more-double-thick"
  );
  openDrawerLinks.forEach(el =>
    el.addEventListener("click", e => {
      e.preventDefault();
      const selectedOption = document.querySelector(
        ".js-listing-page-qty-option.selected"
      );
      const { qty } = selectedOption.dataset;

      window.dispatchEvent(
        new CustomEvent("openDoubleThicknessExplainerDrawer", {
          detail: {
            qty
          }
        })
      );
    })
  );
};

const setupBindingStyleDrawerEventListener = () => {
  const openDrawerLinks = document.querySelectorAll(".js-explain-more-drawer");
  openDrawerLinks.forEach(el =>
    el.addEventListener("click", e => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("openExplainerDrawer"));
    })
  );
};

const setupSustainabilityDrawerEventListener = () => {
  const openDrawerLinks = document.querySelectorAll(
    ".js-sustainability-drawer-cta"
  );
  openDrawerLinks.forEach(el =>
    el.addEventListener("click", e => {
      e.preventDefault();
      window.dispatchEvent(
        new CustomEvent("openSustainabilityExplainerDrawer")
      );
    })
  );
};

const setupThickRadioButtons = () => {
  on(
    "change",
    "#thickness-options input[type='radio'], #thickness-options-mobile input[type='radio']",
    el => {
      const thickEnabled = el.value === "double";
      changeCustomiseUrl({ thick: thickEnabled });
      changeWeddingPricing({ thick: thickEnabled });
      cardThickness = thickEnabled;
      const selectedNameEl = document.querySelector(
        ".js-thickness-selected-name"
      );
      if (selectedNameEl)
        selectedNameEl.textContent = el.dataset.thicknessLabel;
    }
  );
};

const setUpTrackingEventForThickness = () => {
  const customiseCtaButtons = document.querySelectorAll(
    ".js-listing-customise-cta"
  );

  customiseCtaButtons.forEach(customiseCtaButton => {
    customiseCtaButton.addEventListener("click", () => {
      const radioButtonsContainer = document.querySelector(
        "#thickness-options, #thickness-options-mobile"
      );
      const selectedRadioButton = radioButtonsContainer?.querySelector(
        "input[type='radio']:checked"
      );
      if (selectedRadioButton && selectedRadioButton.value === "double") {
        analytics.track("Double Thick Selected From PDP", {
          product_id: radioButtonsContainer.dataset.analyticsId
        });
      }
    });
  });
};

const setupControlsForVideo = videoEl => {
  const playButton = videoEl.parentElement.querySelector(".video-play");
  const progressBar = videoEl.parentElement.querySelector(".video-progress");

  const updateProgress = () => {
    const value = videoEl.currentTime / videoEl.duration;
    progressBar.style.transform = `scaleX(${value})`;
    requestAnimationFrame(updateProgress);
  };

  const play = () => {
    videoEl.play();
    requestAnimationFrame(updateProgress);
  };

  const pause = () => {
    videoEl.pause();
  };

  const hidePlayButton = () => {
    playButton.classList.add("hidden");
  };

  const showPlayButton = () => {
    playButton.classList.remove("hidden");
  };

  playButton.addEventListener("click", play);
  videoEl.addEventListener("click", pause);
  videoEl.addEventListener("play", hidePlayButton);
  videoEl.addEventListener("pause", showPlayButton);
  videoEl.addEventListener("timeupdate", updateProgress);
};

const setupVideos = () => {
  const videos = [...document.querySelectorAll(".product-page-carousel-video")];

  if (videos.length) {
    videos.forEach(video => {
      return setupControlsForVideo(video);
    });
  }
};

const showFullDetails = () => {
  document.body.classList.remove("compact-version");

  const userSignedUp =
    window.sessionStorage.getItem("userSignedUpToMailingList") === "true";

  if (userSignedUp) {
    document.querySelectorAll(".marketing-pop-up-container").forEach(el => {
      el.classList.add("hidden");
    });
  }
};

const updateCalendarUrl = (month, year) => {
  document.querySelectorAll(".js-listing-customise-cta").forEach(cta => {
    const url = new URL(cta.href);
    url.searchParams.set("start_month", month);
    url.searchParams.set("start_year", year);
    cta.href = url.toString();
  });
};

// We want to remove the automatic setting of the calndar start date choices
// on full page reload. It is just used for variant switching
window.localStorage.removeItem("calendarStartOptions");

const syncCalendarStartPills = value => {
  let activeLabel = "";
  document.querySelectorAll(".js-calendar-start-pill").forEach(pill => {
    const active = pill.dataset.value === value;
    pill.classList.toggle("inset-border-ink", active);
    pill.classList.toggle("text-ink", active);
    pill.classList.toggle("text-ink-4", !active);
    if (active) activeLabel = pill.textContent.trim();
  });
  const label = document.querySelector(".js-calendar-start-label");
  if (label) label.textContent = activeLabel;
};

const setupCalendarStartMonthInput = () => {
  const input = document.getElementById("js-calendar-start-select");
  if (!input) return;

  // The server marks the default month as selected, so input.value already holds it
  const savedOptions = window.localStorage.getItem("calendarStartOptions");
  if (savedOptions) {
    const { month, year } = JSON.parse(savedOptions);
    input.value = `${month}-${year}`;
  }

  const [initialMonth, initialYear] = input.value.split("-");
  updateCalendarUrl(initialMonth, initialYear);
  syncCalendarStartPills(input.value);

  input.addEventListener("change", event => {
    const [month, year] = event.target.value.split("-");
    updateCalendarUrl(month, year);
    window.localStorage.setItem(
      "calendarStartOptions",
      JSON.stringify({ month, year })
    );
  });
};
const setupOtherCarousels = () => {
  const carousels = document.querySelectorAll(".stacked-grid");

  carousels.forEach(carousel => {
    const options = {
      cellSelector: ".stacked-grid-group",
      prevNextButtons: true,
      arrowShape:
        "M2.896 52.164A4.716 4.716 0 0 1 .6 48a4.716 4.716 0 0 1 2.292-4.164l41.82-42.464a4.572 4.572 0 0 1 6.536 0c1.8 1.832 1.8 4.804 0 6.636L11.86 48l39.388 39.992c1.8 1.832 1.8 4.8 0 6.636a4.572 4.572 0 0 1-6.536 0L2.896 52.16v.004z"
    };

    const flkty = new Flickity(carousel, options);
    flkty.resize();
    flkty.on("settle", () => flkty.resize());
  });

  // Intersection observer that triggers a resize event every time
  // a carousel is scrolled into view. For some reason, the carousels
  // were not correctly calculating their height on page load and I preferred
  // this to a timeout.
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          window.dispatchEvent(new Event("resize"));
        }
      });
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: 0.1
    }
  );

  carousels.forEach(carousel => {
    observer.observe(carousel);
  });
};

const getFormattedPricesForQuantity = (prices, quantity) => {
  const standardPrice = prices.find(
    price => price.qty === quantity && !price.thickness
  );
  const thickPrice = prices.find(
    price => price.qty === quantity && price.thickness
  );

  return {
    standardPrice: standardPrice
      ? `${standardPrice.currencySymbol}${standardPrice.price.toFixed(2)}`
      : null,
    thickPrice: thickPrice
      ? `${thickPrice.currencySymbol}${thickPrice.price.toFixed(2)}`
      : null
  };
};

const fetchPricesForDoubleThicknessDrawer = async () => {
  const designableTypeElement = document.querySelector(
    ".js-designable-type-id"
  );
  if (!designableTypeElement) return null;

  const { designableTypeId } = designableTypeElement.dataset;

  const response = await api.get(
    `/designable_types/${designableTypeId}/all_pricing_values`
  );
  const { prices } = response.data;

  const selectedOption = document.querySelector(
    ".js-listing-page-qty-option.selected"
  );
  if (!selectedOption) return null;

  const { qty } = selectedOption.dataset;
  const quantity = parseInt(qty, 10);

  return getFormattedPricesForQuantity(prices, quantity);
};

on("click", ".js-flickthrough-link-accordion", () => {
  document
    .querySelector("#flickthrough")
    .scrollIntoView({ behavior: "smooth" });
});

on("turbo:frame-render", "#flickthrough", () => {
  import("./flickthrough").then(() => {
    window.setupFlickthrough();
  });
});

window.initProductPage = () => {
  setupImageGalleryZoom();
  showMoreGalleryImagesPdp();
  setupOtherCarousels();
  setupVideos();
  setupThickRadioButtons();
  setupExplainerDrawerEventListener();
  setupBindingStyleDrawerEventListener();
  setupSustainabilityDrawerEventListener();
  setupThicknessPricingDifference();
  setUpTrackingEventForThickness();
  setupCalendarStartMonthInput();
  window.mountStripeElement?.();
};

window.trackProductView = () => {
  const analyticsData = document.getElementById(
    "product-page-analytics"
  ).dataset;

  window.analytics.track("Viewed Product", {
    ...analyticsData
  });
};

ready(() => {
  window.trackProductView();
  window.initProductPage();

  const isAB205Variant2 = document.body.classList.contains(
    "ab-test-205-active-variant-2"
  );

  const qtyPickersVisible = document.querySelectorAll(QTY_PICKERS);

  document
    .querySelectorAll(".collapsible-variant:not(.expanded)")
    .forEach((variant, index) => {
      variant.classList.add("expanded");

      if (isAB205Variant2) {
        // Variant 2: open only the first option
        if (index === 0 && !qtyPickersVisible.length)
          variant.classList.add("mobile-expanded");
      } else {
        // Variant 1 or control: open all options
        variant.classList.add("mobile-expanded");
      }

      const header = variant.querySelector(".variant-header");
      header?.classList.add("expanded");

      // Variants collapsed on mobile are clamped to their header's real
      // height (headers can wrap, so a fixed height would clip them)
      if (header && !variant.classList.contains("mobile-expanded")) {
        const headerHeight = header.offsetHeight;
        if (headerHeight > 0) {
          variant.style.setProperty(
            "--variant-header-height",
            `${headerHeight}px`
          );
        }
      }
    });

  on("click", ".js-compact-full-details", (_el, event) => {
    const { analyticsId } = event.target.dataset;

    window.analytics.track("Clicked See Full Product Details", {
      analytics_id: analyticsId
    });

    event.preventDefault();
    showFullDetails();

    const details = document.querySelector("#details");
    if (details) {
      details.scrollIntoView({ behavior: "smooth" });
    }
  });

  on("click", ".js-listing-page-qty-option", () => {
    setupThicknessPricingDifference();
  });

  // Copy the main CTA href to the compact paid-lander CTA so both stay in sync
  const mainCta = document.querySelector(
    ".js-listing-customise-cta:not([data-compact-cta])"
  );
  const compactCta = document.querySelector(
    ".js-listing-customise-cta[data-compact-cta]"
  );
  if (
    mainCta instanceof HTMLAnchorElement &&
    compactCta instanceof HTMLAnchorElement
  ) {
    const mainCtaHref = mainCta.getAttribute("href");
    if (mainCtaHref && mainCtaHref.trim() !== "") {
      compactCta.href = mainCtaHref;
    }
  }

  // Update active dot as the mini carousel scrolls
  const miniTrack = document.querySelector(".paid-lander-compact-image-track");
  const miniDots = document.querySelectorAll(
    ".paid-lander-compact-image-dots span"
  );
  if (miniTrack && miniDots.length > 0) {
    miniTrack.addEventListener(
      "scroll",
      () => {
        const activeIndex = Math.round(
          miniTrack.scrollLeft /
            (miniTrack.firstElementChild?.offsetWidth || miniTrack.offsetWidth)
        );
        miniDots.forEach((dot, i) =>
          dot.classList.toggle("active", i === activeIndex)
        );
      },
      { passive: true }
    );
  }
});

window.addEventListener("clickedScrollToFlickthrough", () => {
  const flickthroughFrame = document.querySelector("#flickthrough");

  const scrollToFlickthrough = () => {
    const flickthroughApp =
      flickthroughFrame.querySelector("#flickthrough-app");
    if (flickthroughApp) {
      showFullDetails();
      flickthroughApp.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const scrollWhenVisible = element => {
    if (element.getBoundingClientRect().height > 0) {
      scrollToFlickthrough();
      return;
    }
    let done = false;
    const observer = new ResizeObserver((entries, obs) => {
      if (!entries[0]) return;
      if (entries[0].contentRect.height > 0) {
        done = true;
        obs.disconnect();
        scrollToFlickthrough();
      }
    });
    observer.observe(element);
    setTimeout(() => {
      if (!done) {
        observer.disconnect();
        scrollToFlickthrough();
      }
    }, 2000);
  };

  // Ensures that Flickthrough is scrolled to after lazy loading, whether the frame content is already present or not
  const flickthroughApp = flickthroughFrame.querySelector("#flickthrough-app");
  if (flickthroughApp) {
    scrollWhenVisible(flickthroughApp);
  } else {
    let handledFlickthroughFrameRender = false;

    on("turbo:frame-render", "#flickthrough", () => {
      if (handledFlickthroughFrameRender) return;

      handledFlickthroughFrameRender = true;
      const loadedApp = flickthroughFrame.querySelector("#flickthrough-app");
      if (loadedApp) {
        scrollWhenVisible(loadedApp);
      }
    });
    flickthroughFrame.scrollIntoView({ behavior: "auto" });
  }
});

on("turbo:frame-render", "#related-listings", () => {
  setupOtherCarousels();
});

on("turbo:frame-render", "#recommended-listings", () => {
  window.initializeProductCarousel();
});

on("turbo:frame-render", "#drawer-content", async () => {
  const pricingData = await fetchPricesForDoubleThicknessDrawer();
  if (pricingData) {
    const standardPriceEl = document.querySelector(".js-standard-price");
    const thickPriceEl = document.querySelector(".js-thick-price");

    if (standardPriceEl) {
      standardPriceEl.innerText = pricingData.standardPrice ?? "N/A";
    }

    if (thickPriceEl) {
      thickPriceEl.innerText = pricingData.thickPrice ?? "N/A";
    }
  }
});

on("click", ".variant-header", el => {
  if (desktopMediaQuery.matches) return; // collapsing is mobile-only

  const variant = el.closest(".collapsible-variant");
  const header = el.closest(".variant-header");
  if (!variant || !header) return;
  // Classes alone can lie on mobile (variants load `expanded` but the media
  // query clamps them closed), so read the rendered height instead
  const isExpanded = variant.offsetHeight > header.offsetHeight;

  if (isExpanded) {
    variant.style.height = `${variant.scrollHeight}px`; // set to current height (not auto) so transition can animate
    variant.getBoundingClientRect(); // force reflow so transition has a concrete start value
    variant.style.height = `${header.offsetHeight}px`;
    variant.classList.remove("expanded", "mobile-expanded");
    header.classList.remove("expanded");
  } else {
    variant.style.height = `${variant.scrollHeight}px`;
    variant.classList.add("expanded", "mobile-expanded");
    header.classList.add("expanded");
    const onTransitionEnd = event => {
      if (event.target !== variant || event.propertyName !== "height") return;
      variant.style.height = "auto";
      variant.removeEventListener("transitionend", onTransitionEnd);
    };
    variant.addEventListener("transitionend", onTransitionEnd);
  }
});

on("click", ".js-calendar-start-pill", pill => {
  const { value } = pill.dataset;
  const input = document.getElementById("js-calendar-start-select");
  if (!input) return;
  input.value = value;
  input.dispatchEvent(new Event("change"));
  syncCalendarStartPills(value);
});

// eslint-disable-next-line import/prefer-default-export
export { updateThickPriceOnVariantChange, clearThickState };
